import get from 'lodash/get'
import {createSelector} from 'reselect'

import * as eventsSelectors from '../events'
import * as ordersSelectors from '../orders'

import {
  categoryFitMetaDataTypesMap,
  JACKET_SLEEVE,
  REPLACEMENTS_DAMAGED_ITEM_ISSUE,
  REPLACEMENTS_FIT_ISSUE,
  REPLACEMENTS_MISSING_ITEM_ISSUE,
  REPLACEMENTS_WRONG_ITEM_ISSUE,
  ReplacementsEligibleProductCategoriesForFit,
  ReplacementsItemIssueOptions,
} from './enums'
import {
  formatExchangeRequestAddresses,
  formatExchangeRequestItems,
  formatSizeOptions,
} from './helpers'
import {SHIRTS_CATEGORY_SLUG} from '../../const'

/* Getters */
export const currentEventId = (state) => get(state, 'replacements.currentEventId')
export const currentOrderId = (state) => get(state, 'replacements.currentOrderId')
export const currentStep = (state) => get(state, 'replacements.currentStep')
export const currentItemToExchangeIndex = (state) =>
  get(state, 'replacements.currentItemToExchangeIndex')
export const currentItemIssueIndex = (state) => get(state, 'replacements.currentItemIssueIndex')
export const selectedItemsToExchange = (state) => get(state, 'replacements.selectedItemsToExchange')
export const allSelectedItemsToExchangeIds = (state) =>
  get(selectedItemsToExchange(state), 'allIds')
export const allSelectedItemsToExchangeById = (state) => get(selectedItemsToExchange(state), 'byId')
export const eligibleItemsToExchange = (state) => get(state, 'replacements.eligibleItemsToExchange')
export const selectedFitData = (state) => get(state, 'replacements.selectedFitData')
export const isFetchingEligibleItemsToExchange = (state) =>
  get(eligibleItemsToExchange(state), 'isLoading')
export const fetchingEligibleItemsToExchangeFailed = (state) =>
  get(eligibleItemsToExchange(state), 'error')
export const allEligibleItemsToExchangeIds = (state) =>
  get(eligibleItemsToExchange(state), 'allIds')
export const allEligibleItemsToExchangeById = (state) => get(eligibleItemsToExchange(state), 'byId')
export const productSizes = (state) => get(state, 'replacements.productSizes')
export const isProductSizesFetched = (state) => get(productSizes(state), 'isFetched')
export const allProductSizesIds = (state) => get(productSizes(state), 'allIds')
export const allProductSizesById = (state) => get(productSizes(state), 'byId')
export const shippingInfo = (state) => get(state, 'replacements.shippingInfo')
export const isEditing = (state) => get(state, 'replacements.isEditing')
export const isSubmittingExchangeRequest = (state) =>
  get(state, 'replacements.submitExchangeRequest.isPending')
export const exchangeRequestFailed = (state) =>
  get(state, 'replacements.submitExchangeRequest.error')
export const currentShippingAddress = (state) => ({
  shipping: {
    address: {...get(shippingInfo(state), 'shippingAddress')},
  },
})
export const numDaysFromEvent = (state) => {
  const eventId = currentEventId(state)
  return eventsSelectors.eventDaysAwayById(state, parseInt(eventId, 10))
}
export const shippingMethodMessaging = createSelector(numDaysFromEvent, (numDaysAway) => {
  if (numDaysAway >= 6) {
    return 'Select your preferred shipping method.'
  }

  return 'Requests placed more than 48 hours after order arrival require rush shipping to ensure delivery before your event. Please select your preferred option.'
})

/* Selectors */

// eligibleItemsToExchange
export const allEligibleItemsToExchange = createSelector(
  allEligibleItemsToExchangeIds,
  allEligibleItemsToExchangeById,
  (allIds, byId) => allIds.map((id) => byId[id]),
)

// selectedItemsToExchange
export const allSelectedItemsToExchangeDetails = createSelector(
  allSelectedItemsToExchangeIds,
  allEligibleItemsToExchangeById,
  (selectedItemIds, itemDetailById) => selectedItemIds.map((id) => itemDetailById[id]),
)

export const currentItemToExchangeDetails = createSelector(
  currentItemToExchangeIndex,
  allSelectedItemsToExchangeIds,
  allEligibleItemsToExchangeById,
  (itemToExchangeIndex, selectedItemToExchangeIds, eligibleItemsToExchangeById) =>
    eligibleItemsToExchangeById[selectedItemToExchangeIds[itemToExchangeIndex]],
)

export const isCurrentItemEligibleForFitQuestionnaire = createSelector(
  currentItemToExchangeDetails,
  ({categorySlug}) => ReplacementsEligibleProductCategoriesForFit.includes(categorySlug),
)

export const selectedIssuesForAllItems = createSelector(
  allSelectedItemsToExchangeIds,
  allSelectedItemsToExchangeById,
  (allIds, byId) => allIds.map((id) => byId[id].issues),
)

export const selectedIssuesForCurrentItem = createSelector(
  allSelectedItemsToExchangeIds,
  allSelectedItemsToExchangeById,
  currentItemToExchangeIndex,
  (allItemIds, allItemsById, currItemToExchangeIndex) =>
    get(allItemsById, `[${allItemIds[currItemToExchangeIndex]}].issues`),
)

export const selectedIssuesByTypeForCurrentItem = createSelector(
  allSelectedItemsToExchangeIds,
  allSelectedItemsToExchangeById,
  currentItemToExchangeIndex,
  (allItemIds, allItemsById, currItemToExchangeIndex) =>
    get(allItemsById, `[${allItemIds[currItemToExchangeIndex]}].issuesByType`),
)

export const issuesByTypeForAllSelectedItems = createSelector(
  allSelectedItemsToExchangeIds,
  allSelectedItemsToExchangeById,
  allEligibleItemsToExchangeById,
  (allIds, byId, eligibleItemsToExchangeById) =>
    allIds.map((id) => ({
      ...byId[id].issuesByType,
      general: {
        ...byId[id].issuesByType.general,
        // We shouldn't be displaying the fit issue type when showing
        // list of general issues reported for each item UNLESS the item
        // is not a fit-type item (eg: accessories, sweaters)
        selectedIssueTypes: byId[id].issues
          .filter((issueIndex) => {
            const fitIssueOptionIndex = ReplacementsItemIssueOptions.indexOf(REPLACEMENTS_FIT_ISSUE)
            return (
              issueIndex !== fitIssueOptionIndex ||
              (issueIndex === fitIssueOptionIndex &&
                !ReplacementsEligibleProductCategoriesForFit.includes(
                  eligibleItemsToExchangeById[id].categorySlug,
                ))
            )
          })
          .map((issueIndex) => ReplacementsItemIssueOptions[issueIndex]),
      },
    })),
)

export const selectedFitIssueAnswersForCurrentItem = createSelector(
  currentItemToExchangeDetails,
  selectedIssuesByTypeForCurrentItem,
  ({categorySlug, sizes}, issueTypes) => {
    return get(issueTypes, 'fit', {
      // We want to initialize the values here to the current user's size profile values
      // For the tailoring options, we should initialize the value to 0
      fitQuestionAnswers: categoryFitMetaDataTypesMap[categorySlug].map((metaDataType) => {
        // If the jacket sleeve is null, let's initialize to 0, otherwise set selected option
        // to current value of metadata in user's size profile
        const selectedMetaDataOption =
          metaDataType === JACKET_SLEEVE ? sizes[metaDataType] || 0 : sizes[metaDataType]

        return {
          metaDataType,
          selectedFitOption: 'Just right',
          selectedMetaDataOption,
        }
      }),
    })
  },
)

export const isCollarExtenderNeededForCurrentItem = createSelector(
  currentItemToExchangeDetails,
  selectedIssuesByTypeForCurrentItem,
  ({categorySlug}, issueTypes) =>
    categorySlug !== SHIRTS_CATEGORY_SLUG ? false : get(issueTypes, 'fit.isCollarExtenderNeeded'),
)

export const selectedIssueTypesForCurrentItem = createSelector(
  selectedIssuesForCurrentItem,
  (issuesIndices) => issuesIndices.map((issueIndex) => ReplacementsItemIssueOptions[issueIndex]),
)

export const currentItemHasFitIssue = createSelector(
  selectedIssueTypesForCurrentItem,
  (issueTypesForCurrentItem) => issueTypesForCurrentItem.includes(REPLACEMENTS_FIT_ISSUE),
)

export const currentItemHasNonFitIssues = createSelector(
  selectedIssueTypesForCurrentItem,
  (issueTypesForCurrentItem) =>
    issueTypesForCurrentItem.some(
      (issueType) =>
        issueType === REPLACEMENTS_WRONG_ITEM_ISSUE ||
        issueType === REPLACEMENTS_MISSING_ITEM_ISSUE ||
        issueType === REPLACEMENTS_DAMAGED_ITEM_ISSUE,
    ),
)

// productSizes
export const sizeOptionsForCurrentItem = createSelector(
  currentItemToExchangeDetails,
  allProductSizesById,
  (currentItem, allSizesById) => {
    if (!currentItem.sizes) {
      return {}
    }

    const {productSetId, sizes: userSizeOptions} = currentItem
    const rawSizeOptionsForProductSet = allSizesById[productSetId]

    if (!rawSizeOptionsForProductSet) {
      return {}
    }

    return formatSizeOptions(rawSizeOptionsForProductSet, userSizeOptions)
  },
)

export const createExchangeRequestPayload = createSelector(
  currentOrderId,
  allSelectedItemsToExchangeDetails,
  selectedIssuesForAllItems,
  issuesByTypeForAllSelectedItems,
  shippingInfo,
  (
    orderId,
    allItemDetails,
    allItemSelectedIssueTypes,
    allItemIssueDetails,
    addressFormPayload,
  ) => ({
    orderId,
    items: formatExchangeRequestItems(
      allItemDetails,
      allItemSelectedIssueTypes,
      allItemIssueDetails,
    ),
    shipping: formatExchangeRequestAddresses(addressFormPayload),
  }),
)

export const cannotShowReplacements = createSelector(
  ordersSelectors.isUserOrdersRequestSuccess,
  ordersSelectors.getUniqueUpcomingRentalOrder,
  ($isUserOrdersRequestSuccess, $getUniqueUpcomingRentalOrder) =>
    $isUserOrdersRequestSuccess && !$getUniqueUpcomingRentalOrder,
)
